import { useCallback, useEffect, useState } from "react";
import { ElementItemProps } from "../types";
import { useIntl } from "react-intl";
import { useDevice } from "../../../../utils/DeviceProvider";
import { Grid, Skeleton, Typography } from "@mui/material";
import { ChkboxElement } from "./Chkbox";
import Info from "./Info";
import { RestoreButton, useResetContextMenu } from "../../../../utils/ResetContextMenuProvider";
import { isEqual } from "lodash-es";

export interface OTINENV1Parameters {
  index: number;
  hasTriggers: boolean;
}

interface OTINENV1Value {
  home: boolean | null;
  prefRoaming: boolean | null;
  roaming: boolean | null;
}

const OTINENV1 = ({ index, hasTriggers, configKey, elementKey, disabled }: Omit<ElementItemProps<OTINENV1Parameters>, "label">) => {
  const [active] = useState({ active: true });
  const [configIndex, setConfigIndex] = useState<number | null>(null);
  const [saved, setSaved] = useState<OTINENV1Value | null>(null);
  const [home, setHome] = useState<boolean | null>(null);
  const [prefRoaming, setPrefRoaming] = useState<boolean | null>(null);
  const [roaming, setRoaming] = useState<boolean | null>(null);
  const [loaded, setLoaded] = useState(false);

  const { formatMessage } = useIntl();
  const {
    device: { currentValue, config },
    setCurrentValue,
  } = useDevice();
  const { openContextMenu } = useResetContextMenu();

  const onChange = useCallback(
    ({ home, prefRoaming, roaming, route }: { home?: boolean; prefRoaming?: boolean; roaming?: boolean; route?: string }) => {
      if (configIndex === null) {
        return;
      }
      if (!currentValue[configKey].items[configIndex].value) {
        currentValue[configKey].items[configIndex].value = {};
      }
      if (home !== undefined) {
        currentValue[configKey].items[configIndex].value.home = home;
      }
      if (prefRoaming !== undefined) {
        currentValue[configKey].items[configIndex].value.prefRoaming = prefRoaming;
      }
      if (roaming !== undefined) {
        currentValue[configKey].items[configIndex].value.roaming = roaming;
      }
      if (route !== undefined) {
        currentValue[configKey].items[configIndex].value.route = route;
      }
      setCurrentValue(currentValue);
    },
    [setCurrentValue, currentValue, configKey, configIndex]
  );

  useEffect(() => {
    active.active = true;

    return () => {
      active.active = false;
    };
  }, [active]);

  useEffect(() => {
    if (!currentValue) {
      return;
    }
    const cIndex = currentValue[configKey]?.items?.findIndex(({ type, key }: { type: string; key: string }) => type === "OTINENV1" && (key === elementKey || `${key}` === elementKey)) ?? null;
    setConfigIndex(cIndex);
  }, [configKey, currentValue, elementKey]);

  useEffect(() => {
    if (!currentValue || !config || configIndex === null || configIndex === -1) {
      return;
    }
    const svd = config.toolboxConfig[configKey]?.items?.[configIndex].value ?? null;
    setSaved(svd);
    const { home, prefRoaming, roaming } = currentValue[configKey].items[configIndex]?.value ?? {};
    setHome(home ?? null);
    setPrefRoaming(prefRoaming ?? null);
    setRoaming(roaming ?? null);
    setLoaded(true);
  }, [active, currentValue, config, configIndex, configKey]);

  if (!loaded) {
    return (
      <Grid container spacing={1}>
        <Grid item>
          <Info text={`${index + 1}.`} inlineTitle />
        </Grid>
        {hasTriggers ? (
          <>
            <Grid item>
              <Skeleton variant="rectangular" height={40} sx={{ width: 40, borderRadius: 1 }} />
            </Grid>
            <Grid item>
              <Skeleton variant="rectangular" height={40} sx={{ width: 120, borderRadius: 1 }} />
            </Grid>
            <Grid item>
              <Skeleton variant="rectangular" height={40} sx={{ width: 140, borderRadius: 1 }} />
            </Grid>
          </>
        ) : null}
      </Grid>
    );
  }

  if (configIndex === -1) {
    return <Typography>{formatMessage({ defaultMessage: "Config not found." })}</Typography>;
  }

  return (
    <Grid container spacing={1} sx={{ alignItems: "flex-end" }}>
      <Grid item>
        <Info text={`${index + 1}.`} inlineTitle sx={hasTriggers ? {} : { paddingTop: 1 }} />
      </Grid>
      {hasTriggers ? (
        <>
          <Grid item>
            <ChkboxElement
              value={home}
              label={formatMessage({ defaultMessage: "Home" })}
              onChange={(value) => onChange({ home: value })}
              edited={(saved?.home ?? null) !== home}
              disabled={disabled}
              onContextMenu={saved && saved.home !== home ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.home`) : undefined}
            />
          </Grid>
          <Grid item>
            <ChkboxElement
              value={prefRoaming}
              label={formatMessage({ defaultMessage: "Preferred roaming" })}
              onChange={(value) => onChange({ prefRoaming: value })}
              edited={(saved?.prefRoaming ?? null) !== prefRoaming}
              disabled={disabled}
              onContextMenu={
                saved && saved.prefRoaming !== prefRoaming ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.prefRoaming`) : undefined
              }
            />
          </Grid>
          <Grid item>
            <ChkboxElement
              value={roaming}
              label={formatMessage({ defaultMessage: "Roaming network" })}
              onChange={(value) => onChange({ roaming: value })}
              edited={(saved?.roaming ?? null) !== roaming}
              disabled={disabled}
              onContextMenu={saved && saved.roaming !== roaming ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.roaming`) : undefined}
            />
          </Grid>
          {(!saved ? home || prefRoaming || roaming : !isEqual(saved, { home, prefRoaming, roaming })) ? (
            <Grid item>
              <RestoreButton path={`${configKey}.items.${configIndex}.value`} saved={saved} />
            </Grid>
          ) : null}
        </>
      ) : null}
    </Grid>
  );
};

export default OTINENV1;
